import { Button, Card, Container, Table, TableBody, TableContainer } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { LoadingScreen } from 'src/components/loading-screen';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { useDeleteConsultantMutation, useGetAllConsultantsMutation } from 'src/services/user/consultant-api';
import { TableNoData, TablePaginationCustom, useTable, TableHeadCustom } from 'src/components/table';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { RoleBasedGuard } from 'src/auth/guard';
import { useAuthContext } from 'src/auth/hooks';
import { RouterLink } from 'src/routes/components';
import Iconify from 'src/components/iconify';
import { homeActions } from 'src/store/store';
import { useBoolean } from 'src/hooks/use-boolean';
import ErrorView from 'src/sections/error/error-view';
import QuickStaffTableRow from '../quick-staff-table-row';
import QuickStaffTableToolbar from '../quick-staff-table-toolbar';
import QuickStaffQuickView from '../quick-staff-quick-view';
import QuickStaffForm from '../quick-staff-form';

const TABLE_HEAD = [
    { id: 'name', label: 'Name' },
    { id: 'role', label: 'Role'},
    { id: 'type', label: 'Email'},
    { id: 'contact', label: 'Contact Number'},
    { id: 'access', label: 'Additional Access', align: 'center'},
    { id: '', label: ''}
];

const defaultFilters = {
    name: '',
    ref: ''
};

export default function QuickStaffView() {
    const settings = useSettingsContext();

    const { enqueueSnackbar } = useSnackbar();

    const router = useRouter();

    const dispatch = useDispatch();

    const { isUserRoleHasAccess } = useAuthContext();

    const table = useTable({ defaultOrderBy: 'name' });

    const [tableData, setTableData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);

    const [filters, setFilters] = useState(defaultFilters);
    const [searchTimer, setSearchTimer] = useState(null);
    const [searchKey, setSearchKey] = useState(null);
    const [isShow, setIsShow] = useState(false);

    const [getAllConsultants, { isLoading, isSuccess, data, error }] = useGetAllConsultantsMutation();

    const [deleteConsultant, { error: errorDeleteStaff, isSuccess: isSuccessDeleteStaff }] = useDeleteConsultantMutation();

    const canReset =
        !!filters.name ||
        !!filters.ref;

    const notFound = (!tableData.length && canReset) || !tableData.length;

    const quickView = useBoolean();
    const quickEdit = useBoolean();
    const quickCreate = useBoolean();
    const [viewData, setViewData] = useState(null);

    const handleResetFilters = useCallback(() => {
        setSearchKey(null);
        setIsShow(false);
        setFilters(defaultFilters);
    }, []);

    const handleFilters = useCallback((name, value) => {
        table.onResetPage();

        setSearchKey(value);

        if (searchTimer) {
            clearTimeout(searchTimer);
        }

        if (!value) {
            handleResetFilters();
        }

        const newSearchTimer = setTimeout(() => {
            getAllConsultants({ page: table.page, size: table.rowsPerPage, key: value });
        }, 1000);

        setSearchTimer(newSearchTimer);
    },[getAllConsultants, handleResetFilters, searchTimer, table]);

    useEffect(() => {
        getAllConsultants({ page: table.page, size: table.rowsPerPage, key: searchKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table.page, table.rowsPerPage]);

    const handleDeleteRow = useCallback(
        (id) => {
            deleteConsultant(id);
        },
        [deleteConsultant]
    );

    const handleQuickViewRow = useCallback((row) => {
        dispatch(
            homeActions.setConsultantData(row)
        );

        quickView.onTrue();
    },[dispatch, quickView]);

    const handleEditRow = useCallback((row) => {
        if(!quickEdit.value){
            setViewData(row);
            quickEdit.onTrue();
        }
    },[quickEdit]);

    const handleCreateView = useCallback(() => {
        quickCreate.onTrue();
    },[quickCreate]);

    useEffect(() => {
        if (isSuccess) {
            console.log(data);
            setTableData(data.content)
            setTotalElements(data.meta.total);
        }
    }, [isSuccess, data]);

    useEffect(() => {
        if (errorDeleteStaff) {
            enqueueSnackbar('Deletion Failed!', { variant: 'error' });
        }

        if (isSuccessDeleteStaff) {
            enqueueSnackbar('Successfully deleted the staff!');

            editRefreshCall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enqueueSnackbar, errorDeleteStaff, isSuccessDeleteStaff]);

    const editRefreshCall = useCallback(() => {
        getAllConsultants({ page: table.page, size: table.rowsPerPage, key: searchKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchKey, table.page, table.rowsPerPage]);

    let quickStaffContent;

    if (isLoading) {
        quickStaffContent = <LoadingScreen />
    }
    else if (error) {
        quickStaffContent = <ErrorView error={error?.data} showTryAgain={false}/>
    }
    else if (isSuccess) {
        quickStaffContent = (
            <Card>
                <QuickStaffTableToolbar
                    value={searchKey}
                    onFilters={handleFilters}
                />
                
                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    <Scrollbar>
                        <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }} stickyHeader>
                            <TableHeadCustom
                                order={table.order}
                                orderBy={table.orderBy}
                                headLabel={TABLE_HEAD}
                                rowCount={tableData.length}
                                numSelected={table.selected.length}
                                onSort={table.onSort}
                                onSelectAllRows={(checked) =>
                                table.onSelectAllRows(
                                checked,
                                tableData.map((row) => row.id)
                                )}
                            />

                            <TableBody>
                                {tableData
                                    .map((row) => (
                                        <QuickStaffTableRow
                                            key={row.id}
                                            row={row}
                                            onViewRow={() => handleQuickViewRow(row)}
                                            onEditRow={() => handleEditRow(row)}
                                            onDeleteRow={() => handleDeleteRow(row.id)}
                                        />
                                    ))}
                                <TableNoData notFound={notFound} />
                            </TableBody>
                        </Table>
                    </Scrollbar>
                </TableContainer>

                <TablePaginationCustom
                    count={totalElements}
                    page={table.page}
                    rowsPerPage={table.rowsPerPage}
                    onPageChange={table.onChangePage}
                    onRowsPerPageChange={table.onChangeRowsPerPage}
                    dense={table.dense}
                    onChangeDense={table.onChangeDense}
                />
            </Card>
        );
    }

    return (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
            <CustomBreadcrumbs
                heading="Quick Staff"
                links={[
                    {
                        name: 'Dashboard',
                        href: paths.dashboard.driverReview.root,
                    },
                    {
                        name: 'Quick Staff',
                        href: paths.dashboard.user.quickStaff,
                    },
                ]}
                action={
                    isUserRoleHasAccess(8, 5)
                    ?
                    <Button
                        component={RouterLink}
                        variant="contained"
                        startIcon={<Iconify icon="mingcute:add-line" />}
                        onClick={handleCreateView}
                    >
                        Add Quick Staff
                    </Button>
                    :
                    <></>
                }
                sx={{
                    mb: { xs: 3, md: 5 },
                }}
            />

            <RoleBasedGuard hasContent mainMenuOrder={8} subMenuOrder={1} sx={{ py: 10 }}>  
                {quickStaffContent}

                <QuickStaffQuickView open={quickView.value} onClose={quickView.onFalse} />
                <QuickStaffForm open={quickCreate.value} onClose={quickCreate.onFalse} onSuccess={editRefreshCall}/>
                <QuickStaffForm consultant={viewData} open={quickEdit.value} onClose={quickEdit.onFalse} onSuccess={editRefreshCall}/>
            </RoleBasedGuard>
        </Container>
    )
}